import axios from 'axios';
import { AuthTokens, User } from '../types/auth';

// API URLの設定 - Herokuデプロイを考慮
const API_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:8010/api'
  : `${window.location.origin}/api`;

const authApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエストインターセプター
authApi.interceptors.request.use(
  (config) => {
    const tokens = localStorage.getItem('tokens');
    if (tokens && config.headers) {
      try {
        const { access } = JSON.parse(tokens) as AuthTokens;
        if (access) {
          config.headers.Authorization = `Bearer ${access}`;
        }
      } catch (error) {
        // トークンの解析に失敗した場合は削除
        localStorage.removeItem('tokens');
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // 重要: リクエストにリトライフラグがない場合だけ進む
    if (!originalRequest || originalRequest._isRetry) {
      return Promise.reject(error);
    }

    // アカウント無効の場合 (403)
    // ログインリクエスト中の場合は特別処理 - リダイレクトせずエラーを返す
    if (error.response?.status === 403) {
      // ログイン処理のリクエストの場合は、単にエラーを返す (リダイレクトしない)
      if (originalRequest.url?.includes('/auth/login/')) {
        return Promise.reject(error);
      }
      
      // それ以外の場合は、ログアウトしてリダイレクト
      localStorage.removeItem('tokens');
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
      return Promise.reject(error);
    }
    
    // トークン期限切れの場合（401）かつリトライしていない場合のみ
    if (error.response?.status === 401 && !originalRequest._isRetry) {
      // リトライフラグを設定
      originalRequest._isRetry = true;

      try {
        const tokensStr = localStorage.getItem('tokens');
        if (tokensStr) {
          const tokens = JSON.parse(tokensStr) as AuthTokens;
          if (tokens.refresh) {
            const response = await axios.post<AuthTokens>(`${API_URL}/auth/refresh/`, { 
              refresh: tokens.refresh 
            });
            
            // 新しいトークンを保存
            localStorage.setItem('tokens', JSON.stringify(response.data));
            
            // 元のリクエストを再試行
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
            }
            return authApi(originalRequest);
          }
        }
        throw new Error('リフレッシュトークンがありません');
      } catch (refreshError) {
        // リフレッシュトークンも期限切れの場合
        localStorage.removeItem('tokens');
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const login = async (email: string, password: string): Promise<AuthTokens> => {
  const response = await authApi.post<AuthTokens>('/auth/login/', { email, password });
  return response.data;
};

export const logout = () => {
  localStorage.removeItem('tokens');
};

export const getProfile = async (): Promise<User> => {
  const response = await authApi.get<User>('/auth/profile/');
  return response.data;
};

export const updateProfile = async (userData: Partial<User>): Promise<User> => {
  const response = await authApi.put<User>('/auth/profile/', userData);
  return response.data;
};

export default authApi;